/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Data types and casting", description = "Third-party APIs require the data you send to be of a specific data type. Unless otherwise specified, Hightouch takes the data from your source and leaves the data type unchanged to send to your destination. You can choose to cast data into different types using SQL.", lastUpdatedDate = new Date(1696377600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Data types and casting",
        description: "Third-party APIs require the data you send to be of a specific data type. Unless otherwise specified, Hightouch takes the data from your source and leaves the data type unchanged to send to your destination. You can choose to cast data into different types using SQL.",
        lastUpdatedDate: "Wed Oct 04 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Data types",
          "depth": 0,
          "id": "data-types"
        }, {
          "value": "View model data types",
          "depth": 1,
          "id": "view-model-data-types"
        }, {
          "value": "View destination data types",
          "depth": 1,
          "id": "view-destination-data-types"
        }, {
          "value": "Casting",
          "depth": 0,
          "id": "casting"
        }, {
          "value": "Casting from your model configuration",
          "depth": 1,
          "id": "casting-from-your-model-configuration"
        }, {
          "value": "Casting in SQL",
          "depth": 1,
          "id": "casting-in-sql"
        }, {
          "value": "SQL aliasing",
          "depth": 1,
          "id": "sql-aliasing"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      code: "code",
      a: "a",
      ul: "ul",
      li: "li",
      img: "img",
      h3: "h3",
      strong: "strong",
      ol: "ol",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing data to a destination, their underlying APIs require the data you send to be correctly typed. For example, a ", _jsx(_components.code, {
          children: "Name"
        }), " field may require a ", _jsx(_components.code, {
          children: "string"
        }), " data type, while a ", _jsx(_components.code, {
          children: "timestamp"
        }), " field may require a particular ", _jsx(_components.code, {
          children: "datetime"
        }), " format. If you try to sync an incorrect type, for example, a ", _jsx(_components.code, {
          children: "number"
        }), " type for an identifier, when a ", _jsx(_components.code, {
          children: "string"
        }), " type is expected, APIs usually reject the request, and the sync fails."]
      }), "\n", _jsxs(_components.p, {
        children: ["When you define a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), ", Hightouch doesn't change the data types found in your ", _jsx(_components.a, {
          href: "/getting-started/concepts#sources",
          children: "source"
        }), " unless otherwise specified. To safeguard your syncs from failing, the data types your model returns must align with your destination's data type expectations. If your source data types don't match your destination's expectations, you can use ", _jsx(_components.a, {
          href: "#casting",
          children: "casting"
        }), " while setting up your models."]
      }), "\n", _jsx(_components.h2, {
        id: "data-types",
        children: "Data types"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch recognizes the following data types for ", _jsx(_components.a, {
          href: "#view-model-data-types",
          children: "model columns"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Boolean"
        }), "\n", _jsx(_components.li, {
          children: "Number"
        }), "\n", _jsx(_components.li, {
          children: "String"
        }), "\n", _jsx(_components.li, {
          children: "Timestamp"
        }), "\n", _jsx(_components.li, {
          children: "Date"
        }), "\n", _jsx(_components.li, {
          children: "Object / Array"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["And the following for ", _jsx(_components.a, {
          href: "#view-destination-data-types",
          children: "destination fields"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Boolean"
        }), "\n", _jsx(_components.li, {
          children: "Number"
        }), "\n", _jsx(_components.li, {
          children: "String"
        }), "\n", _jsx(_components.li, {
          children: "Timestamp"
        }), "\n", _jsx(_components.li, {
          children: "Date/time"
        }), "\n", _jsx(_components.li, {
          children: "Associated field (linked to another table)"
        }), "\n", _jsx(_components.li, {
          children: "Null value"
        }), "\n", _jsx(_components.li, {
          children: "Unknown type"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The Hightouch UI uses these icons to distinguish various data types for destination fields:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "working-with-data/data-types-casting-data-icon-key.png",
          alt: "Icons in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "view-model-data-types",
        children: "View model data types"
      }), "\n", _jsxs(_components.p, {
        children: ["If you don't know the data types of the columns returned from your model, you can view them from the ", _jsx(_components.strong, {
          children: "Columns"
        }), " tab on the model's overview page:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "working-with-data/data-types-casting-model.png",
          alt: "Model data types in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can also ", _jsx(_components.a, {
          href: "#casting-from-your-model-configuration",
          children: "change the data types"
        }), " returned from a query here."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Make sure to read through the ", _jsx(_components.a, {
            href: "/models/creating-models#updating-a-primary-key",
            children: "primary key\nupdates"
          }), " section before making\nany changes to your primary key."]
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Hightouch intentionally stringifies your chosen ", _jsx(_components.a, {
            href: "/getting-started/concepts#unique-primary-key-requirement",
            children: _jsx(_components.strong, {
              children: "primary\nkey"
            })
          }), " column for\nenhanced performance during ", _jsx(_components.a, {
            href: "/getting-started/concepts#change-data-capture",
            children: "change data\ncapture"
          }), ". If you need to sync\nthe primary key column as a non-string value, use ", _jsx(_components.a, {
            href: "#sql-alaising",
            children: "SQL\naliasing"
          }), " in your model to create a new column specifically for\nsyncing."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "view-destination-data-types",
        children: "View destination data types"
      }), "\n", _jsxs(_components.p, {
        children: ["When creating a sync configuration, you can view the destination fields' expected data types by looking at their icons when setting up ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mappings"
        }), ":"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "working-with-data/data-types-casting-destination-icons.png",
          alt: "Destination data types in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "casting",
        children: "Casting"
      }), "\n", _jsxs(_components.p, {
        children: ["For your data to reach its destination without error, its data type must match the data type required by the destination. You can use ", _jsx(_components.strong, {
          children: _jsx(_components.a, {
            href: "https://en.wikipedia.org/wiki/Type_conversion",
            children: "type casting"
          })
        }), " to change column data types. You have these options:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click and point casting from a model's ", _jsx(_components.strong, {
            children: "Columns"
          }), " tab: suitable for all ", _jsx(_components.a, {
            href: "/models/creating-models",
            children: "modeling methods"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Casting via SQL in your model definition: suitable if you've built your model using the ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "SQL editor"
          }), " and prefer to encode casting in your SQL query"]
        }), "\n", _jsxs(_components.li, {
          children: ["SQL aliasing: recommended if your ", _jsx(_components.a, {
            href: "/getting-started/concepts#unique-primary-key-requirement",
            children: "primary key"
          }), " data type is incompatible with the expected destination data type"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "casting-from-your-model-configuration",
        children: "Casting from your model configuration"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["From the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview page"]
          }), ", select the model whose columns you want to cast."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Columns"
          }), " tab."]
        }), "\n", _jsx(_components.li, {
          children: "Use the picklist to the right of each column to choose the data type you want for that column."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "working-with-data/data-types-casting.png",
          alt: "Destination data types in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "casting-in-sql",
        children: "Casting in SQL"
      }), "\n", _jsx(_components.p, {
        children: "You can cast values from one data type to another when writing the SQL query that defines a model."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, this query casts a column called ", _jsx(_components.code, {
          children: "total_storage_used_mb"
        }), " as an integer:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CAST"
          }), " (total_storage_used_mb ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "int"
          }), ")\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " public.organizations;\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more on SQL casting, see ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/sql/t-sql/functions/cast-and-convert-transact-sql?view=sql-server-ver16/",
          children: "this guide on SQL type casting"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "sql-aliasing",
        children: "SQL aliasing"
      }), "\n", _jsxs(_components.p, {
        children: ["If your model's primary key isn't a string, Hightouch stringifies it for enhanced record-matching performance. If stringification makes your primary key incompatible with the data type your destination is expecting, you can use ", _jsx(_components.strong, {
          children: "SQL aliasing"
        }), " to create an additional field for mapping."]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, if you use a field called ", _jsx(_components.code, {
          children: "id"
        }), " as your primary key, Hightouch stringifies its value to perform the matching and ", _jsx(_components.a, {
          href: "/getting-started/concepts#change-data-capture",
          children: "diffing operations"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Many destinations expect a field called ", _jsx(_components.code, {
          children: "id"
        }), " to be a string. Some destinations, however, expect ", _jsx(_components.code, {
          children: "id"
        }), " to be an integer. This mismatch causes a data type conflict if you need to map your primary key in addition to using it for matching."]
      }), "\n", _jsxs(_components.p, {
        children: ["Using SQL aliasing and casting can solve this issue. This example query selects the ", _jsx(_components.code, {
          children: "customer_id"
        }), " column twice. The second time, it casts the column as an ", _jsx(_components.code, {
          children: "int"
        }), ", and aliases it as a new ", _jsx(_components.code, {
          children: "id_for_mapping"
        }), " column."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " customer_id, ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CAST"
          }), "(customer_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "int"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " id_for_mapping\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " customers;\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The query results will now include a string ", _jsx(_components.code, {
          children: "customer_id"
        }), " column and an integer ", _jsx(_components.code, {
          children: "id_for_mapping"
        }), " column. You can then use ", _jsx(_components.code, {
          children: "customer_id"
        }), " as the primary key in your model setup and ", _jsx(_components.code, {
          children: "id_for_mapping"
        }), " for field mapping in a sync configuration."]
      })]
    });
  }
}
export default MDXContent;
